import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import api, { publicApi } from '../lib/api';
import { 
  Menu, X, User as UserIcon, ShoppingCart, LogOut, ChevronDown,
  Home, Building, BookOpen, FlaskConical, GraduationCap, Users, Ticket, Shield,
  Heart, Settings, LayoutDashboard, Bell, Gift, ShoppingBag, Briefcase, 
  Calendar, CalendarDays, Mail, Clock, FileText, Wrench, Download, FolderOpen, Monitor,
  Gamepad2, Target, Anchor, Trophy, Rocket, Award, Map, Bot, Layers, 
  MessageSquare, Handshake, HelpCircle, Coins, Share2, Building2, LogIn,
  ShoppingBag as Cart, Compass, EyeOff, Globe, Lock, AlertTriangle, Hash, CheckCircle, Flag, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import ReferralModal from './ReferralModal';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

import { API } from '../config/api';

// Icon mapping for dynamic rendering
const iconMap = {
  Home, Building, BookOpen, FlaskConical, GraduationCap, Users, Ticket, Shield,
  Heart, Settings, LayoutDashboard, Bell, Gift, ShoppingBag, Briefcase, 
  Calendar, CalendarDays, Mail, Clock, FileText, Wrench, Download, FolderOpen, Monitor,
  Gamepad2, Target, Anchor, Trophy, Rocket, Award, Map, Bot, Layers, 
  MessageSquare, Handshake, HelpCircle, Coins, Share2, Building2, LogIn,
  Cart, Compass, EyeOff, User: UserIcon, Flag
};

// Rank tier definitions - Ocean Food Chain (from UserStatsBar)
// 🏆 Squidemy Rank System — HARD MODE (40 Ranks)
// Designed for true depth, not a game ladder
// Large point jumps per tier, slow early progression
const RANK_TIERS = [
  // 🌱 MICROSCOPIC / ORIGIN TIER (0 – 500 XP)
  // "You barely exist. You survive by learning."
  { name: 'Bacteria', minPoints: 0, maxPoints: 100, icon: '🦠', tier: 'Microscopic', tierIcon: '🌱' },
  { name: 'Archaea', minPoints: 101, maxPoints: 250, icon: '🧬', tier: 'Microscopic', tierIcon: '🌱' },
  { name: 'Plankton', minPoints: 251, maxPoints: 500, icon: '🫧', tier: 'Microscopic', tierIcon: '🌱' },
  
  // 🌊 PRIMAL CURRENT TIER (501 – 5,500 XP)
  // "You begin movement, but the ocean decides your fate."
  { name: 'Sea Worm', minPoints: 501, maxPoints: 1500, icon: '🪱', tier: 'Primal Current', tierIcon: '🌊' },
  { name: 'Krill', minPoints: 1501, maxPoints: 2500, icon: '🦐', tier: 'Primal Current', tierIcon: '🌊' },
  { name: 'Urchin', minPoints: 2501, maxPoints: 4000, icon: '🔮', tier: 'Primal Current', tierIcon: '🌊' },
  { name: 'Sea Snail', minPoints: 4001, maxPoints: 5500, icon: '🐚', tier: 'Primal Current', tierIcon: '🌊' },
  
  // 🪸 BENTHIC BEGINNER TIER (5,501 – 12,500 XP)
  // "You live on the ocean floor. Progress is slow and earned."
  { name: 'Hermit Crab', minPoints: 5501, maxPoints: 7500, icon: '🦀', tier: 'Benthic Beginner', tierIcon: '🪸' },
  { name: 'Crab', minPoints: 7501, maxPoints: 9500, icon: '🦀', tier: 'Benthic Beginner', tierIcon: '🪸' },
  { name: 'Lobster', minPoints: 9501, maxPoints: 11500, icon: '🦞', tier: 'Benthic Beginner', tierIcon: '🪸' },
  { name: 'Snapper', minPoints: 11501, maxPoints: 12500, icon: '🐟', tier: 'Benthic Beginner', tierIcon: '🪸' },
  
  // 🧠 ADAPTIVE SURVIVOR TIER (12,501 – 20,500 XP)
  // "You develop strategy, patience, and defense."
  { name: 'Jellyfish', minPoints: 12501, maxPoints: 14500, icon: '🪼', tier: 'Adaptive Survivor', tierIcon: '🧠' },
  { name: 'Starfish', minPoints: 14501, maxPoints: 16500, icon: '⭐', tier: 'Adaptive Survivor', tierIcon: '🧠' },
  { name: 'Seahorse', minPoints: 16501, maxPoints: 18500, icon: '🐴', tier: 'Adaptive Survivor', tierIcon: '🧠' },
  { name: 'Lionfish', minPoints: 18501, maxPoints: 20500, icon: '🦁', tier: 'Adaptive Survivor', tierIcon: '🧠' },
  
  // 🐠 SIGNAL & CAMOUFLAGE TIER (20,501 – 28,500 XP)
  // "You learn misdirection, signaling, and teamwork."
  { name: 'Clownfish', minPoints: 20501, maxPoints: 22500, icon: '🐠', tier: 'Signal & Camouflage', tierIcon: '🐠' },
  { name: 'Pufferfish', minPoints: 22501, maxPoints: 24500, icon: '🐡', tier: 'Signal & Camouflage', tierIcon: '🐠' },
  { name: 'Penguin', minPoints: 24501, maxPoints: 26500, icon: '🐧', tier: 'Signal & Camouflage', tierIcon: '🐠' },
  { name: 'Eel', minPoints: 26501, maxPoints: 28500, icon: '🐍', tier: 'Signal & Camouflage', tierIcon: '🐠' },
  
  // 🐢 ENDURANCE & MEMORY TIER (28,501 – 36,500 XP)
  // "Longevity matters. You remember your failures."
  { name: 'Sea Turtle', minPoints: 28501, maxPoints: 32500, icon: '🐢', tier: 'Endurance & Memory', tierIcon: '🐢' },
  { name: 'Grouper', minPoints: 32501, maxPoints: 36500, icon: '🐟', tier: 'Endurance & Memory', tierIcon: '🐢' },
  
  // 🦑 PROBLEM-SOLVER TIER (36,501 – 44,500 XP)
  // "True intelligence emerges."
  { name: 'Squid', minPoints: 36501, maxPoints: 40500, icon: '🦑', tier: 'Problem-Solver', tierIcon: '🦑' },
  { name: 'Octopus', minPoints: 40501, maxPoints: 44500, icon: '🐙', tier: 'Problem-Solver', tierIcon: '🦑' },
  
  // ⚔️ PREDATOR STRATEGIST TIER (44,501 – 56,500 XP)
  // "Speed, precision, and intent."
  { name: 'Barracuda', minPoints: 44501, maxPoints: 48500, icon: '🐟', tier: 'Predator Strategist', tierIcon: '⚔️' },
  { name: 'Tuna', minPoints: 48501, maxPoints: 52500, icon: '🐟', tier: 'Predator Strategist', tierIcon: '⚔️' },
  { name: 'Swordfish', minPoints: 52501, maxPoints: 56500, icon: '⚔️', tier: 'Predator Strategist', tierIcon: '⚔️' },
  
  // 🌊 OCEAN GLIDER TIER (56,501 – 68,500 XP)
  // "Grace, awareness, and control."
  { name: 'Stingray', minPoints: 56501, maxPoints: 60500, icon: '🛸', tier: 'Ocean Glider', tierIcon: '🌊' },
  { name: 'Manta Ray', minPoints: 60501, maxPoints: 64500, icon: '🛸', tier: 'Ocean Glider', tierIcon: '🌊' },
  { name: 'Seal', minPoints: 64501, maxPoints: 68500, icon: '🦭', tier: 'Ocean Glider', tierIcon: '🌊' },
  
  // 🧭 HIGH INTELLIGENCE TIER (68,501 – 88,500 XP)
  // "You lead and communicate."
  { name: 'Sea Lion', minPoints: 68501, maxPoints: 74500, icon: '🦭', tier: 'High Intelligence', tierIcon: '🧭' },
  { name: 'Dolphin', minPoints: 74501, maxPoints: 80500, icon: '🐬', tier: 'High Intelligence', tierIcon: '🧭' },
  { name: 'Orca', minPoints: 80501, maxPoints: 88500, icon: '🐋', tier: 'High Intelligence', tierIcon: '🧭' },
  
  // 🦈 APEX PREDATOR TIER (88,501 – 108,500 XP)
  // "No mistakes allowed."
  { name: 'Shark', minPoints: 88501, maxPoints: 98500, icon: '🦈', tier: 'Apex Predator', tierIcon: '🦈' },
  { name: 'Walrus', minPoints: 98501, maxPoints: 108500, icon: '🦭', tier: 'Apex Predator', tierIcon: '🦈' },
  
  // 🐋 OCEAN TITAN TIER (108,501 – 140,500 XP)
  // "Mass, knowledge, and presence."
  { name: 'Narwhal', minPoints: 108501, maxPoints: 124500, icon: '🦄', tier: 'Ocean Titan', tierIcon: '🐋' },
  { name: 'Whale', minPoints: 124501, maxPoints: 140500, icon: '🐋', tier: 'Ocean Titan', tierIcon: '🐋' },
  
  // 🌑 ABYSSAL MYTH TIER (140,501 – 220,500 XP)
  // "Few ever reach this depth."
  { name: 'Fangtooth', minPoints: 140501, maxPoints: 180500, icon: '👹', tier: 'Abyssal Myth', tierIcon: '🌑' },
  { name: 'Giant Squid', minPoints: 180501, maxPoints: 220500, icon: '🦑', tier: 'Abyssal Myth', tierIcon: '🌑' },
  
  // 👑 ELDRITCH LEGEND TIER (220,501+ XP)
  { name: 'Kraken', minPoints: 220501, maxPoints: 350000, icon: '🐙', tier: 'Eldritch Legend', tierIcon: '👑' },
  { name: 'Leviathan', minPoints: 350001, maxPoints: Infinity, icon: '👑', tier: 'Eldritch Legend', tierIcon: '👑' }
];

const getRankTier = (points) => {
  return RANK_TIERS.find(tier => points >= tier.minPoints && points <= tier.maxPoints) || RANK_TIERS[0];
};

const Navigation = () => {
  const { user: authUser, cartCount: authCartCount, subscriptionAccess: authSubscriptionAccess, squidTourStatus, logout: authLogout, isAuthenticated } = useAuth();
  
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(authUser);
  const [cartCount, setCartCount] = useState(authCartCount || 0);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [pages, setPages] = useState([]);
  const [pagesLoaded, setPagesLoaded] = useState(false);  // Track if visibility data is loaded
  const [navCategories, setNavCategories] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [squidTourComplete, setSquidTourComplete] = useState(squidTourStatus?.completed ?? true);
  const [showTourIndicator, setShowTourIndicator] = useState(false);
  const [userStats, setUserStats] = useState({ squid_coins: 0, points: 0, xp: 0, completed: 0, rankTier: RANK_TIERS[0], studentId: null });
  const [worldProgress, setWorldProgress] = useState({});
  const [subscriptionAccess, setSubscriptionAccess] = useState(authSubscriptionAccess || { 
    subscription_tier: null, 
    subscription_level: 0, 
    subscription_name: 'Guest',
    page_access: {},
    is_military: false 
  });
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Sync with AuthContext
  useEffect(() => {
    setUser(authUser);
    setCartCount(authCartCount || 0);
    setSubscriptionAccess(authSubscriptionAccess || subscriptionAccess);
    setSquidTourComplete(squidTourStatus?.completed ?? true);
    
    // Calculate user stats from auth user data
    if (authUser) {
      const userXP = authUser?.xp || authUser?.total_xp || 0;
      const userPoints = authUser?.total_points || authUser?.points || 0;
      const totalScore = userXP + userPoints;
      const rankTier = getRankTier(totalScore);
      const completed = (authUser?.labs_completed || 0) + (authUser?.challenges_won || 0);
      
      setUserStats({
        squid_coins: authUser?.squid_coins || 0,
        points: totalScore,
        xp: userXP,
        completed: completed,
        rankTier: rankTier,
        studentId: authUser?.student_number || authUser?.student_id || null
      });
    }
  }, [authUser, authCartCount, authSubscriptionAccess, squidTourStatus]);
  
  // Fetch world progress (only if authenticated)
  const fetchWorldProgress = async () => {
    if (!authUser) return;
    try {
      // Use authenticated api instance to include Supabase token
      const response = await api.get('/user/world-progress');
      setWorldProgress(response.data || {});
    } catch (error) {
      setWorldProgress({});
    }
  };

  // Fetch subscription access for page locks
  const fetchSubscriptionAccess = async () => {
    try {
      // Use authenticated api instance to include Supabase token
      const response = await api.get('/user/subscription-access');
      const accessData = response.data || {
        subscription_tier: null,
        subscription_level: 0,
        subscription_name: 'Guest',
        page_access: {},
        is_military: false
      };
      setSubscriptionAccess(accessData);
      // Cache for Footer and other components
      sessionStorage.setItem('subscription_access', JSON.stringify(accessData));
    } catch (error) {
      console.error('Failed to fetch subscription access:', error);
      setSubscriptionAccess({
        subscription_tier: null,
        subscription_level: 0,
        subscription_name: 'Guest',
        page_access: {},
        is_military: false
      });
    }
  };

  // Remove old fetchUser - now using AuthContext

  const fetchPageVisibility = async () => {
    try {
      // Add timeout to prevent blocking navigation render
      // Use publicApi since visibility doesn't require auth
      const response = await publicApi.get('/pages/visibility', {
        timeout: 2000  // 2 second timeout
      });
      
      setPages(response.data.pages || []);
      setNavCategories(response.data.nav_categories || {});
      setPagesLoaded(true);
    } catch (error) {
      console.log('Navigation visibility check skipped');
      setPagesLoaded(true);  // Mark as loaded even on error to prevent blocking UI
    }
  };

  useEffect(() => {
    fetchPageVisibility();
    if (authUser) {
      fetchWorldProgress();
    }
  }, [authUser]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleLogout = async () => {
    try {
      await authLogout();
    } catch (error) {
      // Force clear local storage as backup
      localStorage.removeItem('auth_token');
      localStorage.removeItem('supabase_auth_token');
      sessionStorage.removeItem('cached_user');
    }
    // Always reset user state and navigate, regardless of errors
    setUser(null);
    navigate('/');
  };

  // Helper to check if user is admin
  const isUserAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  // Helper to check if user is a debugger (QA testers - can see everything except admin)
  const isDebugger = user?.is_debugger === true;

  // World unlock requirements - order matters!
  // UPDATED Jan 2026: SquidTour SUSPENDED - removed from unlock chain
  const WORLD_UNLOCK_ORDER = [
    // { id: 'squidtour', name: 'SquidTour', path: '/worlds/squidtour', icon: 'Compass', requires: null, alwaysOpen: true }, // SUSPENDED
    { id: 'squidcapture', name: 'SquidCapture', path: '/capture-the-squid?tab=squidcapture', icon: 'Flag', requires: null, alwaysOpen: true },
    { id: 'squidslife', name: 'SquidLife', path: '/capture-the-squid?tab=squidslife', icon: 'Gamepad2', requires: null, alwaysOpen: true }, // No longer requires squidtour
    { id: 'squidland', name: 'SquidLand', path: '/capture-the-squid?tab=squidland', icon: 'Anchor', requires: 'squidslife' },
    { id: 'squidtrench', name: 'SquidTrench', path: '/capture-the-squid?tab=squidtrench', icon: 'Anchor', requires: 'squidland' },
  ];

  // Check if a world is unlocked
  const isWorldUnlocked = (worldId) => {
    if (!user) return true; // Guests see all as accessible
    if (isUserAdmin || isDebugger) return true; // Admins/debuggers bypass
    
    const world = WORLD_UNLOCK_ORDER.find(w => w.id === worldId);
    if (!world) return true;
    if (world.alwaysOpen) return true;
    if (!world.requires) return true;
    
    // Check if required world is complete
    const requiredWorld = worldProgress[world.requires];
    if (requiredWorld && requiredWorld.completion_percentage >= 100) return true;
    
    // SquidTour check removed - tour is suspended
    return true; // All worlds accessible now
  };

  // Check if a world is completed
  const isWorldCompleted = (worldId) => {
    if (!user) return false;
    if (worldId === 'squidtour') return true; // Always complete - suspended
    const world = worldProgress[worldId];
    return world && world.completion_percentage >= 100;
  };

  // Check subscription-based page access - NOW DISABLED (Pay-As-You-Go model)
  // All pages are accessible after completing SquidTour, users just pay with coins
  const getPageSubscriptionStatus = (path) => {
    // No subscription requirements anymore - everyone has access
    return { hasAccess: true, requiredTier: null };
  };

  // UPDATED Jan 2026: SquidTour SUSPENDED - no links are locked anymore
  const isLinkLocked = (path) => {
    return false; // Nothing is locked - tour suspended
  };

  // Handle click on locked link - now just passes through
  const handleLockedLinkClick = (e, path) => {
    return true; // Always allow - tour suspended
  };

  // Filter pages based on visibility, auth requirements, and user role
  // UPDATED: Only show PUBLISHED pages in navigation for ALL users (including admins)
  // Admins can still access unpublished pages by typing the URL directly
  const getVisiblePages = (categoryKey) => {
    // Don't show any pages until visibility data is loaded (prevents flash)
    if (!pagesLoaded) return [];
    
    return pages
      .filter(page => {
        // Must be in this category
        if (page.nav_category !== categoryKey) return false;
        
        // STRICT: Must be published - no exceptions for nav menu
        // Admins can access unpublished pages via direct URL, but they won't appear in nav
        if (!page.is_published) return false;
        
        // Must show in nav
        if (!page.show_in_nav) return false;
        
        // Check auth requirements
        if (page.requires_auth && !user) return false;
        
        // Check admin requirements (only show admin pages to admins)
        if (page.requires_admin && !isUserAdmin) return false;
        
        return true;
      })
      .sort((a, b) => (a.nav_order || 0) - (b.nav_order || 0));
  };

  // Get visible categories (those that have visible pages)
  const getVisibleCategories = () => {
    return Object.entries(navCategories)
      .filter(([key, cat]) => {
        // Skip hidden category
        if (key === 'hidden') return false;
        // Skip home (it's just a direct link)
        if (key === 'home') return false;
        // Check if category requires auth
        if (cat.requires_auth && !user) return false;
        // Check if category requires admin
        if (cat.requires_admin && !isUserAdmin) return false;
        // Must have visible pages
        const visiblePages = getVisiblePages(key);
        return visiblePages.length > 0;
      })
      .sort((a, b) => (a[1].order || 0) - (b[1].order || 0));
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || FileText;
    return IconComponent;
  };

  // Check if a page path is published (for nav/footer visibility)
  // Pages not in the visibility system are shown by default (not controlled)
  // Only hide pages that are explicitly set to is_published=false
  const isPagePublished = (path) => {
    // Find the page in the visibility list
    const page = pages.find(p => {
      // Exact match (including query params)
      if (p.path === path) return true;
      // Check base paths without query params
      const itemBasePath = path.split('?')[0];
      const pageBasePath = p.path.split('?')[0];
      if (pageBasePath === itemBasePath) return true;
      return false;
    });
    
    // If page not in visibility system, assume it's visible (not controlled)
    if (!page) return true;
    
    // Only check is_published - no admin bypass for menus
    return page.is_published === true;
  };

  // Filter menu items based on page visibility
  const filterPublishedItems = (items) => {
    return items.filter(item => isPagePublished(item.path));
  };

  const visibleCategories = getVisibleCategories();
  
  return (
    <nav className="sticky top-0 z-[100] bg-[#0a1628] border-b border-slate-700/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-36 py-2">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-1" data-testid="logo-link">
            <picture>
              <source srcSet="/squiddy_logo.webp" type="image/webp" />
              <img 
                src="/squiddy_logo.png" 
                alt="Squidemy Logo" 
                className="w-[128px] h-[128px] object-contain"
                width="128"
                height="128"
                loading="eager"
              />
            </picture>
            <div className="flex flex-col -ml-1">
              <span 
                className="text-4xl md:text-5xl font-bold text-white" 
                style={{ fontFamily: "'AstroSpace', sans-serif", letterSpacing: '0.08em' }}
              >
                SQUIDEMY
              </span>
              <span 
                className="text-[13px] font-bold uppercase"
                style={{ background: 'linear-gradient(90deg, #06b6d4, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '0.12em' }}
              >
                IT & Cybersecurity Training
              </span>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1" ref={dropdownRef}>

            {/* SquidPlatform Mega Menu - Tools & Services (triggered from stats bar) */}
            {user && openDropdown === 'squid-platform' && (
              <div className="fixed left-1/2 -translate-x-1/2 top-36 bg-[#0d1a2d] border border-pink-500/30 rounded-2xl shadow-2xl shadow-pink-500/20 p-6 z-[200]" style={{ width: '1200px', maxWidth: '95vw' }}>
                    {/* Header */}
                    <div className="text-center mb-5 pb-4 border-b border-pink-500/20">
                      <h3 className="text-lg font-bold bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent">SquidPlatform Tools</h3>
                      <p className="text-xs text-muted-foreground mt-1">Everything you need to succeed</p>
                    </div>
                    <div className="grid grid-cols-6 gap-4">
                      {/* Column 1: Learn */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-lg border border-emerald-500/20">
                          <span className="text-lg">📚</span>
                          <span className="font-bold text-emerald-400 text-sm uppercase tracking-wide">Learn</span>
                        </div>
                        <div className="space-y-1">
                        {filterPublishedItems([
                          { name: 'SquidCourse', path: '/courses', icon: 'BookOpen', isPublic: true },
                          { name: 'SquidCamps', path: '/bootcamps', icon: 'GraduationCap', isPublic: true },
                          { name: 'SquidPrep', path: '/cert-prep', icon: 'Award', isPublic: true },
                          { name: 'SquidLabs', path: '/labs', icon: 'FlaskConical' },
                          { name: 'SquidCards', path: '/flashcards', icon: 'Layers' },
                          { name: 'SquidStudy', path: '/study', icon: 'BookOpen' },
                        ]).map((item) => {
                          const ItemIcon = getIcon(item.icon);
                          const isTourLocked = isLinkLocked(item.path);
                          const { hasAccess: hasSubAccess, requiredTier } = getPageSubscriptionStatus(item.path);
                          const isActive = location.pathname === item.path;
                          const showSubLock = user && !hasSubAccess && requiredTier && !item.isPublic;
                          const showGreenCheck = item.isPublic;
                          const showSubCheck = !showGreenCheck && user && hasSubAccess;
                          return (
                            <div key={item.path} className="relative group">
                              <Link
                                to={item.path}
                                onClick={(e) => {
                                  if (!handleLockedLinkClick(e, item.path)) return;
                                  setOpenDropdown(null);
                                }}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 hover:bg-white/5 hover:translate-x-1 ${isTourLocked || showSubLock ? 'opacity-60' : ''} ${isActive ? 'bg-primary/20 text-primary border-l-2 border-primary' : ''}`}
                              >
                                <ItemIcon className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                                <span className="text-sm font-medium flex-1">{item.name}</span>
                                {isTourLocked && <Lock className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                                {!isTourLocked && showSubLock && <Lock className="w-5 h-5 text-red-400 flex-shrink-0" />}
                                {!isTourLocked && showGreenCheck && <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />}
                                {!isTourLocked && !showGreenCheck && showSubCheck && <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />}
                              </Link>
                              {showSubLock && (
                                <div className="absolute left-full top-0 ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                  Requires {requiredTier}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        </div>
                      </div>
                      
                      {/* Column 2: Practice */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500/10 to-violet-500/10 rounded-lg border border-purple-500/20">
                          <span className="text-lg">🎮</span>
                          <span className="font-bold text-purple-400 text-sm uppercase tracking-wide">Practice</span>
                        </div>
                        <div className="space-y-1">
                        {filterPublishedItems([
                          { name: 'SquidCloud', path: '/squidcloud', icon: 'Monitor', isPublic: true },
                          { name: 'Capture the Squid', path: '/capture-the-squid', icon: 'Target' },
                          { name: 'SquidPardy', path: '/squidpardy', icon: 'Gamepad2' },
                          { name: 'SquidTrench', path: '/squidtrench', icon: 'Anchor' },
                          { name: 'SquidReady', path: '/interview-prep', icon: 'Users' },
                          { name: 'SquidWorlds', path: '/worlds', icon: 'Compass' },
                        ]).map((item) => {
                          const ItemIcon = getIcon(item.icon);
                          const isTourLocked = isLinkLocked(item.path);
                          const { hasAccess: hasSubAccess, requiredTier } = getPageSubscriptionStatus(item.path);
                          const isActive = location.pathname === item.path;
                          const showSubLock = user && !hasSubAccess && requiredTier && !item.isPublic;
                          const showGreenCheck = item.isPublic;
                          const showSubCheck = !showGreenCheck && user && hasSubAccess;
                          return (
                            <div key={item.path} className="relative group">
                              <Link
                                to={item.path}
                                onClick={(e) => {
                                  if (!handleLockedLinkClick(e, item.path)) return;
                                  setOpenDropdown(null);
                                }}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 hover:bg-white/5 hover:translate-x-1 ${isTourLocked || showSubLock ? 'opacity-60' : ''} ${isActive ? 'bg-primary/20 text-primary border-l-2 border-primary' : ''}`}
                              >
                                <ItemIcon className="w-5 h-5 text-purple-400 flex-shrink-0" />
                                <span className="text-sm font-medium flex-1">{item.name}</span>
                                {isTourLocked && <Lock className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                                {!isTourLocked && showSubLock && <Lock className="w-5 h-5 text-red-400 flex-shrink-0" />}
                                {!isTourLocked && showGreenCheck && <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />}
                                {!isTourLocked && !showGreenCheck && showSubCheck && <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />}
                              </Link>
                              {showSubLock && (
                                <div className="absolute left-full top-0 ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                  Requires {requiredTier}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        </div>
                      </div>

                      {/* Column 3: Career */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 rounded-lg border border-blue-500/20">
                          <span className="text-lg">💼</span>
                          <span className="font-bold text-blue-400 text-sm uppercase tracking-wide">Career</span>
                        </div>
                        <div className="space-y-1">
                        {filterPublishedItems([
                          { name: 'SquidJobs', path: '/jobs', icon: 'Briefcase', isPublic: true },
                          { name: 'SquidIntern', path: '/internships', icon: 'GraduationCap', isPublic: true },
                          { name: 'Consulting', path: '/consulting', icon: 'Monitor', isPublic: true },
                          { name: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
                          { name: 'Book a Session', path: '/booking', icon: 'Calendar', isPublic: true },
                          { name: 'SquidFolio', path: '/resume-lab', icon: 'FileText' },
                          { name: 'SquidGuide', path: '/mentorship', icon: 'Users' },
                          { name: 'SquidMap', path: '/roadmap', icon: 'Map' },
                          { name: 'SquidCerts', path: '/certificates', icon: 'Award' },
                        ]).map((item) => {
                          const ItemIcon = getIcon(item.icon);
                          const isTourLocked = isLinkLocked(item.path);
                          const { hasAccess: hasSubAccess, requiredTier } = getPageSubscriptionStatus(item.path);
                          const isActive = location.pathname === item.path;
                          const showSubLock = user && !hasSubAccess && requiredTier && !item.isPublic;
                          const showGreenCheck = item.isPublic;
                          const showSubCheck = !showGreenCheck && user && hasSubAccess;
                          return (
                            <div key={item.path} className="relative group">
                              <Link
                                to={item.path}
                                onClick={(e) => {
                                  if (!handleLockedLinkClick(e, item.path)) return;
                                  setOpenDropdown(null);
                                }}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 hover:bg-white/5 hover:translate-x-1 ${isTourLocked || showSubLock ? 'opacity-60' : ''} ${isActive ? 'bg-primary/20 text-primary border-l-2 border-primary' : ''}`}
                              >
                                <ItemIcon className="w-5 h-5 text-blue-400 flex-shrink-0" />
                                <span className="text-sm font-medium flex-1">{item.name}</span>
                                {isTourLocked && <Lock className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                                {!isTourLocked && showSubLock && <Lock className="w-5 h-5 text-red-400 flex-shrink-0" />}
                                {!isTourLocked && showGreenCheck && <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />}
                                {!isTourLocked && !showGreenCheck && showSubCheck && <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />}
                              </Link>
                              {showSubLock && (
                                <div className="absolute left-full top-0 ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                  Requires {requiredTier}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        </div>
                      </div>

                      {/* Column 4: Community */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-cyan-500/10 to-teal-500/10 rounded-lg border border-cyan-500/20">
                          <span className="text-lg">🌐</span>
                          <span className="font-bold text-cyan-400 text-sm uppercase tracking-wide">Community</span>
                        </div>
                        <div className="space-y-1">
                        {filterPublishedItems([
                          { name: 'SquidEvents', path: '/events', icon: 'CalendarDays', isPublic: true },
                          { name: 'SquidNews', path: '/blog', icon: 'FileText', isPublic: true },
                          { name: 'SquidPedia', path: '/squidpedia', icon: 'BookOpen' },
                          { name: 'SquidLeaders', path: '/leaderboard', icon: 'Trophy' },
                          { name: 'SquidFriends', path: '/partnership', icon: 'Handshake', isPublic: true },
                          { name: 'SquidTeach', path: '/teach', icon: 'GraduationCap', isPublic: true },
                        ]).map((item) => {
                          const ItemIcon = getIcon(item.icon);
                          const isTourLocked = isLinkLocked(item.path);
                          const { hasAccess: hasSubAccess, requiredTier } = getPageSubscriptionStatus(item.path);
                          const isActive = location.pathname === item.path;
                          const showSubLock = user && !hasSubAccess && requiredTier && !item.isPublic;
                          const showGreenCheck = item.isPublic;
                          const showSubCheck = !showGreenCheck && user && hasSubAccess;
                          return (
                            <div key={item.path} className="relative group">
                              <Link
                                to={item.path}
                                onClick={(e) => {
                                  if (!handleLockedLinkClick(e, item.path)) return;
                                  setOpenDropdown(null);
                                }}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 hover:bg-white/5 hover:translate-x-1 ${isTourLocked || showSubLock ? 'opacity-60' : ''} ${isActive ? 'bg-primary/20 text-primary border-l-2 border-primary' : ''}`}
                              >
                                <ItemIcon className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                                <span className="text-sm font-medium flex-1">{item.name}</span>
                                {isTourLocked && <Lock className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                                {!isTourLocked && showSubLock && <Lock className="w-5 h-5 text-red-400 flex-shrink-0" />}
                                {!isTourLocked && showGreenCheck && <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />}
                                {!isTourLocked && !showGreenCheck && showSubCheck && <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />}
                              </Link>
                              {showSubLock && (
                                <div className="absolute left-full top-0 ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                  Requires {requiredTier}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        </div>
                      </div>

                      {/* Column 5: Marketplace */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-orange-500/10 to-amber-500/10 rounded-lg border border-orange-500/20">
                          <span className="text-lg">🛒</span>
                          <span className="font-bold text-orange-400 text-sm uppercase tracking-wide">Marketplace</span>
                        </div>
                        <div className="space-y-1">
                        {filterPublishedItems([
                          { name: 'SquidMerch', path: '/shop', icon: 'ShoppingBag', isPublic: true },
                          { name: 'SquidCoins', path: '/buy-coins', icon: 'Coins' },
                          { name: 'SquidVouch', path: '/vouchers', icon: 'Gift' },
                          { name: 'SquidDrive', path: '/library', icon: 'FolderOpen' },
                          { name: 'SquidTools', path: '/tools', icon: 'Wrench' },
                          { name: 'SquidGives', path: '/donate', icon: 'Heart', isPublic: true },
                        ]).map((item) => {
                          const ItemIcon = getIcon(item.icon);
                          const isTourLocked = isLinkLocked(item.path);
                          const { hasAccess: hasSubAccess, requiredTier } = getPageSubscriptionStatus(item.path);
                          const isActive = location.pathname === item.path;
                          const showSubLock = user && !hasSubAccess && requiredTier && !item.isPublic;
                          const showGreenCheck = item.isPublic;
                          const showSubCheck = !showGreenCheck && user && hasSubAccess;
                          return (
                            <div key={item.path} className="relative group">
                              <Link
                                to={item.path}
                                onClick={(e) => {
                                  if (!handleLockedLinkClick(e, item.path)) return;
                                  setOpenDropdown(null);
                                }}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 hover:bg-white/5 hover:translate-x-1 ${isTourLocked || showSubLock ? 'opacity-60' : ''} ${isActive ? 'bg-primary/20 text-primary border-l-2 border-primary' : ''}`}
                              >
                                <ItemIcon className="w-5 h-5 text-orange-400 flex-shrink-0" />
                                <span className="text-sm font-medium flex-1">{item.name}</span>
                                {isTourLocked && <Lock className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                                {!isTourLocked && showSubLock && <Lock className="w-5 h-5 text-red-400 flex-shrink-0" />}
                                {!isTourLocked && showGreenCheck && <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />}
                                {!isTourLocked && !showGreenCheck && showSubCheck && <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />}
                              </Link>
                              {showSubLock && (
                                <div className="absolute left-full top-0 ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                  Requires {requiredTier}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        </div>
                      </div>

                      {/* Column 6: Company */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-rose-500/10 to-pink-500/10 rounded-lg border border-rose-500/20">
                          <span className="text-lg">🏢</span>
                          <span className="font-bold text-rose-400 text-sm uppercase tracking-wide">Company</span>
                        </div>
                        <div className="space-y-1">
                        {filterPublishedItems([
                          { name: 'SquidFamily', path: '/about', icon: 'Building', isPublic: true },
                          { name: 'SquidHire', path: '/careers', icon: 'Briefcase', isPublic: true },
                          { name: 'SquidContact', path: '/contact', icon: 'Mail', isPublic: true },
                          { name: 'SquidHelp', path: '/support', icon: 'HelpCircle', isPublic: true },
                          { name: 'SquidTickets', path: '/helpdesk', icon: 'Ticket', isPublic: true },
                          { name: 'SquidBug', path: '/squidbug', icon: 'AlertTriangle', isPublic: true },
                        ]).map((item) => {
                          const ItemIcon = getIcon(item.icon);
                          const isTourLocked = isLinkLocked(item.path);
                          const { hasAccess: hasSubAccess, requiredTier } = getPageSubscriptionStatus(item.path);
                          const isActive = location.pathname === item.path;
                          const showSubLock = user && !hasSubAccess && requiredTier && !item.isPublic;
                          const showGreenCheck = item.isPublic;
                          const showSubCheck = !showGreenCheck && user && hasSubAccess;
                          return (
                            <div key={item.path} className="relative group">
                              <Link
                                to={item.path}
                                onClick={(e) => {
                                  if (!handleLockedLinkClick(e, item.path)) return;
                                  setOpenDropdown(null);
                                }}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 hover:bg-white/5 hover:translate-x-1 ${isTourLocked || showSubLock ? 'opacity-60' : ''} ${isActive ? 'bg-primary/20 text-primary border-l-2 border-primary' : ''}`}
                              >
                                <ItemIcon className="w-5 h-5 text-rose-400 flex-shrink-0" />
                                <span className="text-sm font-medium flex-1">{item.name}</span>
                                {isTourLocked && <Lock className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                                {!isTourLocked && showSubLock && <Lock className="w-5 h-5 text-red-400 flex-shrink-0" />}
                                {!isTourLocked && showGreenCheck && <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />}
                                {!isTourLocked && !showGreenCheck && showSubCheck && <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />}
                              </Link>
                              {showSubLock && (
                                <div className="absolute left-full top-0 ml-2 px-2 py-1 bg-slate-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                  Requires {requiredTier}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        </div>
                      </div>
                    </div>
                  </div>
            )}
          </div>
          
          {/* Right Side Actions - Aligned with SQUIDEMY text */}
          <div className="hidden md:flex items-start space-x-2 mt-0">
            {/* User Stats Bar - Inline, height matches SQUIDEMY text block */}
            {user && (
              <div className="flex items-center gap-4 px-5 py-5 bg-muted/50 rounded-lg border border-border/50">
                {/* SquidPlatform Link */}
                <button 
                  onClick={() => setOpenDropdown(openDropdown === 'squid-platform' ? null : 'squid-platform')}
                  className="flex items-center gap-2 hover:text-pink-400 transition-colors"
                  title="SquidPlatform"
                >
                  <Sparkles className="w-4 h-4 text-pink-500" />
                  <span className="text-sm font-semibold text-foreground">SquidPlatform</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${openDropdown === 'squid-platform' ? 'rotate-180' : ''}`} />
                </button>
                <div className="w-px h-5 bg-border" />
                {/* SquidJourney Link - respects page visibility */}
                {/* Coins */}
                <Link to="/buy-coins" className="flex items-center gap-2 hover:text-primary transition-colors" title="Squid Coins">
                  <Coins className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-semibold text-foreground">{userStats.squid_coins}</span>
                </Link>
                <div className="w-px h-5 bg-border" />
                {/* Cart */}
                <Link to="/cart" className="relative flex items-center gap-2 hover:text-primary transition-colors" title="Shopping Cart">
                  <ShoppingCart className="w-4 h-4" />
                  <span className="text-sm font-semibold text-foreground">Cart</span>
                  {cartCount > 0 && (
                    <span className="bg-primary text-white text-xs px-1.5 py-0.5 rounded-full font-bold min-w-[20px] text-center">
                      {cartCount > 9 ? '9+' : cartCount}
                    </span>
                  )}
                </Link>
              </div>
            )}
            
            {/* User Menu or Login */}
            {user ? (
              <div className="relative group">
                <button className="flex items-center gap-2 p-1.5 hover:bg-accent rounded-full transition-colors">
                  {user.picture || user.profile_picture ? (
                    <img 
                      src={user.picture || user.profile_picture} 
                      alt="" 
                      className="rounded-full object-cover border-2 border-primary/40 hover:border-primary transition-colors"
                      style={{ width: '52px', height: '52px' }}
                    />
                  ) : (
                    <div 
                      className="rounded-full bg-primary/20 flex items-center justify-center border-2 border-primary/40"
                      style={{ width: '52px', height: '52px' }}
                    >
                      <UserIcon className="w-6 h-6 text-primary" />
                    </div>
                  )}
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {/* Wide Multi-Column User Dropdown */}
                <div className={`absolute right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[200] ${(user.role === 'admin' || user.role === 'super_admin') ? 'w-[580px]' : 'w-[520px]'}`}>
                  {/* Single Cohesive User Panel */}
                  <div className="p-4 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 rounded-t-xl border-b border-border">
                    <div className="flex items-center gap-4">
                      {/* User Avatar */}
                      {user.picture || user.profile_picture ? (
                        <img 
                          src={user.picture || user.profile_picture} 
                          alt="" 
                          className="rounded-full object-cover border-2 border-primary/50 shadow-lg shadow-primary/20"
                          style={{ width: '56px', height: '56px' }}
                        />
                      ) : (
                        <div 
                          className="rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center border-2 border-primary/50 shadow-lg shadow-primary/20"
                          style={{ width: '56px', height: '56px' }}
                        >
                          <UserIcon className="w-7 h-7 text-white" />
                        </div>
                      )}
                      
                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-lg font-bold text-foreground truncate">{user.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                        {isDebugger && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 text-xs font-semibold mt-1">
                            🔧 Debugger
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons Row */}
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      
                      {/* SQUIDBOARD Button - Only visible to employees or higher (not students) */}
                      {user.squidboard_access && user.role !== 'student' && user.role !== 'user' && (
                        <Link 
                          to="/squidboard" 
                          onClick={() => setOpenDropdown(null)}
                          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:from-violet-500 hover:to-purple-500 transition-all shadow-lg shadow-violet-500/30"
                        >
                          <Layers className="h-4 w-4" />
                          SQUIDBOARD
                        </Link>
                      )}

                      {/* Blackboard LMS */}
                      <a 
                        href="https://www.squidemy.com/blackboard" 
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setOpenDropdown(null)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-slate-700 to-slate-800 text-white hover:from-slate-600 hover:to-slate-700 transition-all shadow-lg shadow-slate-500/20"
                        data-testid="blackboard-link"
                      >
                        <GraduationCap className="h-4 w-4" />
                        Blackboard
                      </a>
                    </div>
                    
                    {/* Stats Row - Flexible layout for student ID */}
                    <div className="mt-4 pt-3 border-t border-border">
                      {/* Student ID - Full width row if present */}
                      {userStats.studentId && (
                        <Link to="/profile" className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors" title={`Student ID: ${userStats.studentId}`}>
                          <Hash className="text-purple-500 dark:text-purple-400 w-4 h-4 flex-shrink-0" />
                          <span className="text-sm font-bold text-purple-600 dark:text-purple-300">{userStats.studentId}</span>
                        </Link>
                      )}
                      {/* Other stats in grid */}
                      <div className="grid grid-cols-4 gap-2">
                        <Link to="/buy-coins" className="flex items-center gap-1.5 px-2 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors justify-center">
                          <Coins className="text-yellow-500 dark:text-yellow-400 w-3.5 h-3.5 flex-shrink-0" />
                          <span className="text-xs font-bold text-yellow-600 dark:text-yellow-300 truncate">{userStats.squid_coins.toLocaleString()}</span>
                        </Link>
                        <Link to="/leaderboard" className="flex items-center gap-1.5 px-2 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors justify-center">
                          <Trophy className="text-amber-500 dark:text-amber-400 w-3.5 h-3.5 flex-shrink-0" />
                          <span className="text-xs font-bold text-amber-600 dark:text-amber-300 truncate">{userStats.points.toLocaleString()} XP</span>
                        </Link>
                        <div className="flex items-center gap-1.5 px-2 py-2 rounded-lg bg-muted justify-center">
                          <CheckCircle className="text-green-500 dark:text-green-400 w-3.5 h-3.5 flex-shrink-0" />
                          <span className="text-xs font-bold text-green-600 dark:text-green-300 truncate">{userStats.completed.toLocaleString()}</span>
                        </div>
                        <Link to="/leaderboard" className="flex items-center gap-1.5 px-2 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors justify-center">
                          <span className="text-sm flex-shrink-0">{userStats.rankTier.icon}</span>
                          <span className="text-xs font-bold text-primary truncate">{userStats.rankTier.name}</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                  
                  {/* Logout Footer */}
                  <div className="p-3 border-t border-border bg-muted/50 rounded-b-xl">
                    <button 
                      onClick={handleLogout}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-red-500/10 hover:bg-red-500/20 transition-colors text-red-600 dark:text-red-400 border border-red-500/20"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button 
                    variant="outline"
                    className="font-semibold px-8 py-6 text-base rounded-sm border-2 border-purple-600 text-purple-600 bg-white hover:bg-purple-100 hover:text-purple-800 transition-all"
                  >
                    Log in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="font-semibold px-8 py-6 text-base rounded-sm text-white hover:opacity-90 transition-all"
                    style={{ backgroundColor: '#752ec3' }}
                  >
                    Sign up
                  </Button>
                </Link>
              </div>
            )}
          </div>
          
          {/* Mobile menu button - shows below 768px */}
          <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        
        {/* Mobile Menu - shows below 768px */}
        {isOpen && (
          <div className="md:hidden py-4 border-t max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col space-y-1">
              {/* Guest Navigation - Show for non-logged-in users */}
              {!user && (
                <div className="space-y-1 pb-4">
                  <Link 
                    to="/" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-accent rounded-lg text-base font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    <Home className="w-5 h-5 text-cyan-400" />
                    Home
                  </Link>
                  <Link 
                    to="/bootcamps" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-accent rounded-lg text-base font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    <GraduationCap className="w-5 h-5 text-purple-400" />
                    Bootcamps
                  </Link>
                  <Link 
                    to="/courses" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-accent rounded-lg text-base font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    <BookOpen className="w-5 h-5 text-green-400" />
                    Courses
                  </Link>
                  <Link 
                    to="/cert-prep" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-accent rounded-lg text-base font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    <Award className="w-5 h-5 text-yellow-400" />
                    Cert Prep
                  </Link>
                  <Link 
                    to="/roadmap" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-accent rounded-lg text-base font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    <Map className="w-5 h-5 text-blue-400" />
                    Career Roadmap
                  </Link>
                  <Link 
                    to="/about" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-accent rounded-lg text-base font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    <Building className="w-5 h-5 text-orange-400" />
                    About Us
                  </Link>
                  <Link 
                    to="/contact" 
                    className="flex items-center gap-3 px-4 py-3 hover:bg-accent rounded-lg text-base font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    <Mail className="w-5 h-5 text-pink-400" />
                    Contact
                  </Link>
                  
                  {/* Auth buttons for mobile */}
                  <div className="pt-4 px-4 space-y-3 border-t border-border mt-4">
                    <Link to="/login" onClick={() => setIsOpen(false)}>
                      <Button variant="outline" className="w-full font-semibold py-5 text-base border-2 border-purple-600 text-purple-600">
                        Log in
                      </Button>
                    </Link>
                    <Link to="/register" onClick={() => setIsOpen(false)}>
                      <Button className="w-full font-semibold py-5 text-base text-white" style={{ backgroundColor: '#752ec3' }}>
                        Sign up
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
              
              {/* SquidMap (Worlds) Section - Only show for logged-in users */}
              {user && (
                <div className="space-y-1">
                  <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <span>🗺️</span>
                    SquidMap
                  </div>
                  <Link 
                    to="/orientation" 
                    className="flex items-center gap-2 px-6 py-2 hover:bg-accent rounded-lg text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="text-lg">🎓</span>
                    SquidTour
                  </Link>
                  <Link 
                    to="/squids-life" 
                    className="flex items-center gap-2 px-6 py-2 hover:bg-accent rounded-lg text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="text-lg">🦑</span>
                    SquidLife
                  </Link>
                  <Link 
                    to="/worlds/squidland" 
                    className="flex items-center gap-2 px-6 py-2 hover:bg-accent rounded-lg text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="text-lg">🏝️</span>
                    SquidLand
                  </Link>
                  <Link 
                    to="/worlds/squidworld" 
                    className="flex items-center gap-2 px-6 py-2 hover:bg-accent rounded-lg text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="text-lg">🌍</span>
                    SquidWorld
                  </Link>
                  <Link 
                    to="/squid-trench" 
                    className="flex items-center gap-2 px-6 py-2 hover:bg-accent rounded-lg text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="text-lg">🕳️</span>
                    SquidTrench
                  </Link>
                  <Link 
                    to="/squidverse" 
                    className="flex items-center gap-2 px-6 py-2 hover:bg-accent rounded-lg text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="text-lg">🌌</span>
                    SquidVerse
                  </Link>
                </div>
              )}
              
              {/* SquidJourney Categories - Only show for logged-in users */}
              {user && (
                <>
                  {/* Resources */}
                  <div className="space-y-1">
                    <div className="px-3 py-2 text-xs font-semibold text-primary uppercase tracking-wider flex items-center gap-2">
                      <span>🏛️</span> Resources
                    </div>
                    {[
                      { name: 'SquidFamily', path: '/about', icon: 'Building' },
                      { name: 'SquidJourney', path: '/timeline', icon: 'Clock' },
                      { name: 'SquidPedia', path: '/squidpedia', icon: 'BookOpen' },
                      { name: 'SquidNews', path: '/blog', icon: 'FileText' },
                      { name: 'SquidContact', path: '/contact', icon: 'Mail' },
                    ].map((item) => {
                      const ItemIcon = getIcon(item.icon);
                      return (
                        <Link key={item.path} to={item.path} className="flex items-center gap-2 px-6 py-2 hover:bg-accent rounded-lg text-sm" onClick={() => setIsOpen(false)}>
                          <ItemIcon className="w-4 h-4 text-muted-foreground" />
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                  
                  {/* Learning */}
                  <div className="space-y-1">
                    <div className="px-3 py-2 text-xs font-semibold text-primary uppercase tracking-wider flex items-center gap-2">
                      <span>📚</span> Learning
                    </div>
                    {[
                      { name: 'SquidCourse', path: '/courses', icon: 'GraduationCap' },
                      { name: 'SquidPrep', path: '/cert-prep', icon: 'Award' },
                      { name: 'SquidMap', path: '/roadmap', icon: 'Map' },
                      { name: 'SquidCerts', path: '/certificates', icon: 'Award' },
                      { name: 'SquidRank', path: '/leaderboard', icon: 'Trophy' },
                      { name: 'SquidTeach', path: '/teach', icon: 'GraduationCap' },
                    ].map((item) => {
                      const ItemIcon = getIcon(item.icon);
                      return (
                        <Link key={item.path} to={item.path} className="flex items-center gap-2 px-6 py-2 hover:bg-accent rounded-lg text-sm" onClick={() => setIsOpen(false)}>
                          <ItemIcon className="w-4 h-4 text-muted-foreground" />
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                  
                  {/* Growth */}
                  <div className="space-y-1">
                    <div className="px-3 py-2 text-xs font-semibold text-primary uppercase tracking-wider flex items-center gap-2">
                      <span>🧪</span> Growth
                    </div>
                    {[
                      { name: 'SquidCloud', path: '/squidcloud', icon: 'Cloud' },
                      { name: 'SquidLabs', path: '/labs', icon: 'FlaskConical' },
                      { name: 'SquidVMs', path: '/hands-on-labs', icon: 'Monitor' },
                      { name: 'SquidStudy', path: '/study', icon: 'BookOpen' },
                      { name: 'SquidCards', path: '/flashcards', icon: 'Layers' },
                      { name: 'SquidInterview', path: '/interview-prep', icon: 'MessageSquare' },
                      { name: 'SquidWorld', path: '/worlds', icon: 'Globe' },
                    ].map((item) => {
                      const ItemIcon = getIcon(item.icon);
                      return (
                        <Link key={item.path} to={item.path} className="flex items-center gap-2 px-6 py-2 hover:bg-accent rounded-lg text-sm" onClick={() => setIsOpen(false)}>
                          <ItemIcon className="w-4 h-4 text-muted-foreground" />
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                  
                  {/* Discover */}
                  <div className="space-y-1">
                    <div className="px-3 py-2 text-xs font-semibold text-primary uppercase tracking-wider flex items-center gap-2">
                      <span>💼</span> Discover
                    </div>
                    {[
                      { name: 'SquidHire', path: '/careers', icon: 'Briefcase' },
                      { name: 'SquidJobs', path: '/jobs', icon: 'Briefcase' },
                      { name: 'SquidIntern', path: '/internships', icon: 'GraduationCap' },
                      { name: 'SquidGuidance', path: '/mentorship', icon: 'Handshake' },
                      { name: 'SquidFolio', path: '/resume-lab', icon: 'FileText' },
                      { name: 'SquidMeet', path: '/schedule', icon: 'Calendar' },
                    ].map((item) => {
                      const ItemIcon = getIcon(item.icon);
                      return (
                        <Link key={item.path} to={item.path} className="flex items-center gap-2 px-6 py-2 hover:bg-accent rounded-lg text-sm" onClick={() => setIsOpen(false)}>
                          <ItemIcon className="w-4 h-4 text-muted-foreground" />
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                  
                  {/* Marketplace */}
                  <div className="space-y-1">
                    <div className="px-3 py-2 text-xs font-semibold text-primary uppercase tracking-wider flex items-center gap-2">
                      <span>🛒</span> Marketplace
                    </div>
                    {[
                      { name: 'SquidMerch', path: '/shop', icon: 'ShoppingBag' },
                      { name: 'SquidVouchers', path: '/vouchers', icon: 'Ticket' },
                      { name: 'SquidGives', path: '/donate', icon: 'Heart' },
                      { name: 'SquidDrive', path: '/library', icon: 'FolderOpen' },
                      { name: 'SquidDrop', path: '/downloads', icon: 'Download' },
                      { name: 'SquidTools', path: '/tools', icon: 'Wrench' },
                    ].map((item) => {
                      const ItemIcon = getIcon(item.icon);
                      return (
                        <Link key={item.path} to={item.path} className="flex items-center gap-2 px-6 py-2 hover:bg-accent rounded-lg text-sm" onClick={() => setIsOpen(false)}>
                          <ItemIcon className="w-4 h-4 text-muted-foreground" />
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                  
                  {/* Collective */}
                  <div className="space-y-1">
                    <div className="px-3 py-2 text-xs font-semibold text-primary uppercase tracking-wider flex items-center gap-2">
                      <span>🤝</span> Collective
                    </div>
                    {[
                      { name: 'SquidFriends', path: '/partnership', icon: 'Handshake' },
                      { name: 'SquidCoins', path: '/buy-coins', icon: 'Coins' },
                      { name: 'SquidHelp', path: '/support', icon: 'HelpCircle' },
                      { name: 'SquidTickets', path: '/helpdesk', icon: 'Ticket' },
                      { name: 'SquidBug', path: '/squidbug', icon: 'AlertTriangle' },
                    ].map((item) => {
                      const ItemIcon = getIcon(item.icon);
                      return (
                        <Link key={item.path} to={item.path} className="flex items-center gap-2 px-6 py-2 hover:bg-accent rounded-lg text-sm" onClick={() => setIsOpen(false)}>
                          <ItemIcon className="w-4 h-4 text-muted-foreground" />
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                </>
              )}
              
              {/* Auth Section - Only show for logged-in users */}
              {user && (
                <div className="pt-4 mt-4 border-t space-y-1">
                  <>
                    <div className="px-3 py-2 flex items-center gap-3">
                      {user.profile_picture ? (
                        <img src={user.profile_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-primary" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    {/* SQUIDBOARD - Mobile - Only visible to employees or higher */}
                    {user.squidboard_access && user.role !== 'student' && user.role !== 'user' && (
                      <Link 
                        to="/squidboard" 
                        className="flex items-center gap-2 px-3 py-2 text-violet-400 font-medium hover:bg-accent rounded-lg"
                        onClick={() => setIsOpen(false)}
                      >
                        <Layers className="w-4 h-4" />
                        SQUIDBOARD
                      </Link>
                    )}
                    {/* Blackboard LMS - Mobile */}
                    <a 
                      href="https://www.squidemy.com/blackboard" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 text-slate-300 font-medium hover:bg-accent rounded-lg"
                      onClick={() => setIsOpen(false)}
                      data-testid="blackboard-link-mobile"
                    >
                      <GraduationCap className="w-4 h-4" />
                      Blackboard
                    </a>
                    <button 
                      onClick={() => { handleLogout(); setIsOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-red-500 hover:bg-accent rounded-lg"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Referral Modal */}
      <ReferralModal isOpen={showReferralModal} onClose={() => setShowReferralModal(false)} />
    </nav>
  );
};

export default Navigation;
